import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, IconButton } from '@mui/material';
import useUserStore from '@store/useUserStore';
import { useSnackbar } from 'contexts/SnackbarContext';
import { getKeywordSearch } from 'api/mainTab';
import searchIcon from 'assets/images/icons/ic_search.svg';
import alertIcon from 'assets/images/icons/alert.svg';
import { getMessagePaperPlaneIcon } from 'assets/images/icons/messagePaperPlane';
import {getSportsTypes} from 'api/class';
import { updateUserData, getBrandList, updateHomeBrand } from 'api/user';
import { linkOpen } from 'utils/util';
import { MESSAGING_ENABLED } from 'constants/featureFlags';
import './HomeHeader.scss';

const DEBOUNCE_DELAY = 100;
// SubHeader(마이 버클/커뮤니티) 아이콘과 동일한 회색 톤으로 맞춘다
const messageIcon = getMessagePaperPlaneIcon('#464a4d');

function HomeHeader({
    notificationCount = 0,
    messageNotificationCount = 0,
    onBrandClick,
    onNotificationClick,
}) {
  const { userInfo, setUserInfo, setHomeBrand } = useUserStore();
  // const name = userInfo?.name;
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [category, setCategory] = useState([])
  const [selectedCategory, setSelectedCategory] = useState();
  const [brandList, setBrandList] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const searchRef = useRef(null);
  const abortControllerRef = useRef(null);


  useEffect(() => {
    if (category.length > 0) {
      const selected = category.find((c) => c.id === userInfo?.preferred_sport) ?? category[0];
      setSelectedCategory(selected);
      return;
    }
    getSportsTypes().then((res) => {
      if (Array.isArray(res.sports)){
        const selected = res.sports.find((c) => c.id === userInfo?.preferred_sport) ?? res.sports[0];
        setCategory(res.sports);
        setSelectedCategory(selected);
      };
    });
  }, [category, userInfo?.preferred_sport]);

  useEffect(() => {
    if (!isDropdownOpen || brandList.length > 0) return;
    getBrandList().then((res) => {
      if (Array.isArray(res)) setBrandList(res);
    });
  }, [isDropdownOpen, brandList.length]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotification = () => {
    if (onNotificationClick) onNotificationClick();
    else navigate('/alert');
  };

  const handleMessages = () => {
    navigate('/messages');
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setIsDropdownOpen(false);
    // 비로그인 상태에서는 userInfo를 만들지 않는다.
    // (truthy인 nickname 없는 객체가 생기면 헤더 렌더 시 userInfo.nickname[0]에서 크래시)
    // 종목 표시는 위 selectedCategory 로컬 state가 담당한다.
    if (userInfo) {
      setUserInfo({ ...userInfo, preferred_sport: category.id, preferred_sport_name: category.name });
      updateUserData({ preferred_sport: category.id }).catch(console.error);
    }
    if (onBrandClick) onBrandClick(category);
  };

  const handleBrandSelect = async (brand) => {
    if (!userInfo) return;
    if (userInfo?.home_brand_id === brand.id) {
      setIsDropdownOpen(false);
      return;
    }
    const prevUserInfo = userInfo;
    const prevHomeBrand = useUserStore.getState().homeBrand;
    const homeBrand = { home_brand_id: brand.id, home_brand_name: brand.name };
    // userInfo: 헤더 표시/active 체크용, homeBrand: MainPage 필터·강습요청 payload용 — 둘 다 갱신해야 정합.
    setUserInfo({ ...userInfo, ...homeBrand });
    setHomeBrand(homeBrand);
    setIsDropdownOpen(false);
    try {
      await updateHomeBrand(brand.id);
      showSuccess(`소속 브랜드가 '${brand.name}'로 변경되었어요`);
    } catch (err) {
      setUserInfo(prevUserInfo);
      setHomeBrand(prevHomeBrand);
      const code = err?.response?.data?.code;
      if (code === 'BRAND_NOT_FOUND') {
        showError('존재하지 않거나 비활성 브랜드입니다');
      } else {
        showError('브랜드 변경에 실패했어요');
      }
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim()) {
      setSearchResults([]);
      setIsSearchOpen(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      try {
        const data = await getKeywordSearch(value, abortControllerRef.current.signal);
        const results = Array.isArray(data) ? data : (data.results ?? []);
        setSearchResults(results);
        setIsSearchOpen(results.length > 0);
      } catch (error) {
        if (error.code !== 'ERR_CANCELED') console.error('검색 실패:', error);
      }
    }, DEBOUNCE_DELAY);
  };

  const handleResultClick = (result) => {
    setIsSearchOpen(false);
    linkOpen(result.link, navigate)
  };

  return (
    <div className="home-header">
      <div className="home-header__top">
        <div className="home-header__brand-wrapper" ref={dropdownRef}>
          <button
            className="home-header__brand"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
          >
            {userInfo &&  <span className='home-header__profile-circle'>
            {userInfo.profile_image_url ? <img src={userInfo.profile_image_url} alt="프로필" />:
            <span>{userInfo.nickname?.[0]}</span>}

            </span>}
            <span className="home-header__brand-name">
              {(userInfo?.home_brand_name?? '미정') + ' • '}
              {selectedCategory?.name}
            </span>
            <span className={`home-header__brand-arrow${isDropdownOpen ? ' home-header__brand-arrow--open' : ''}`}>▼</span>
          </button>

          {isDropdownOpen && (
            <div className="home-header__dropdown home-header__dropdown--columns">
              <div className="home-header__dropdown-col">
                <div className="home-header__dropdown-title">브랜드</div>
                <ul className="home-header__dropdown-list">
                  {brandList.length === 0 && (
                    <li className="home-header__dropdown-empty">불러오는 중…</li>
                  )}
                  {brandList.map((b) => (
                    <li
                      key={b.id}
                      className={`home-header__dropdown-item${userInfo?.home_brand_id === b.id ? ' home-header__dropdown-item--active' : ''}${!userInfo ? ' home-header__dropdown-item--disabled' : ''}`}
                      onClick={() => handleBrandSelect(b)}
                    >
                      <span>{b.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="home-header__dropdown-col">
                <div className="home-header__dropdown-title">선호 스포츠</div>
                <ul className="home-header__dropdown-list">
                  {category.map((cat) => (
                    <li
                      key={cat.name}
                      className={`home-header__dropdown-item${selectedCategory?.name === cat.name ? ' home-header__dropdown-item--active' : ''}`}
                      onClick={() => handleCategorySelect(cat)}
                    >
                      <span>{cat.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="home-header__actions">
          {MESSAGING_ENABLED && (
            <IconButton
              className="home-header__message"
              onClick={handleMessages}
              size="small"
              aria-label={messageNotificationCount > 0 ? `메시지 ${messageNotificationCount}개 읽지 않음` : "메시지"}
            >
              <Badge
                badgeContent={messageNotificationCount > 0 ? messageNotificationCount : null}
                color="error"
                max={9}
              >
                <img src={messageIcon} alt="" className="home-header__message-icon" />
              </Badge>
            </IconButton>
          )}
          <IconButton
            className="home-header__bell"
            onClick={handleNotification}
            size="small"
            aria-label="알림"
          >
            <Badge
              badgeContent={notificationCount > 0 ? notificationCount : null}
              color="error"
              max={9}
            >
              <img src={alertIcon} alt="" className="home-header__bell-icon" />
            </Badge>
          </IconButton>
        </div>
      </div>


      <div className="home-header__search-wrapper" ref={searchRef}>
        <div className={`home-header__search${isSearchOpen && searchResults.length > 0 ? ' home-header__search--open' : ''}`}>
          <img src={searchIcon} alt="검색" className="home-header__search-icon" />
          <input
            className="home-header__search-input"
            type="text"
            placeholder="찾는 강습, 투어, 제품을 검색해보세요"
            value={searchValue}
            onChange={handleSearchChange}
          />
        </div>

        {isSearchOpen && searchResults.length > 0 && (
          <ul className="home-header__search-results">
            {searchResults.map((result) => (
              <li
                key={`${result.type}-${result.id}`}
                className="home-header__search-result-item"
                onClick={() => handleResultClick(result)}
              >
                <span className="home-header__search-result-badge">{result.type_display}</span>
                <span className="home-header__search-result-desc">{result.name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default HomeHeader;
